import { QueryClient } from '@tanstack/react-query';
import {
  queryKeys,
  invalidationPatterns,
  prefetchStrategies,
  CACHE_CONFIG
} from './cacheConfig';
import {
  teamsApi,
  playersApi,
  gamesApi,
  standingsApi,
  championshipsApi,
  championsApi,
  coachesApi
} from '../services/api';

export class CacheManager {
  private queryClient: QueryClient;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  // Prefetch team-related data when navigating to team detail
  async prefetchTeamData(teamSlug: string) {
    const prefetchPromises = [
      // Prefetch team roster
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.teamRoster(teamSlug),
        queryFn: () => teamsApi.getRoster(teamSlug),
        ...CACHE_CONFIG.SEMI_STATIC,
      }),

      // Prefetch team schedule
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.teamSchedule(teamSlug),
        queryFn: () => teamsApi.getSchedule(teamSlug),
        ...CACHE_CONFIG.DYNAMIC,
      }),

      // Prefetch team stats
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.teamStats(teamSlug),
        queryFn: () => teamsApi.getStats(teamSlug),
        ...CACHE_CONFIG.DYNAMIC,
      }),

      // Prefetch team championships
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.teamChampionships(teamSlug),
        queryFn: () => teamsApi.getChampionships(teamSlug),
        ...CACHE_CONFIG.STATIC,
      }),
    ];

    await Promise.allSettled(prefetchPromises);
  }

  // Prefetch player-related data when navigating to player detail
  async prefetchPlayerData(playerSlug: string, position?: string) {
    const prefetchPromises = [];

    // Prefetch related players by position
    if (position) {
      prefetchPromises.push(
        this.queryClient.prefetchQuery({
          queryKey: queryKeys.playersByPosition(position),
          queryFn: () => playersApi.getByPosition(position),
          ...CACHE_CONFIG.SEMI_STATIC,
        })
      );
    }

    // Prefetch active players list
    prefetchPromises.push(
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.playersActive(),
        queryFn: () => playersApi.getActive(),
        ...CACHE_CONFIG.SEMI_STATIC,
      })
    );

    await Promise.allSettled(prefetchPromises);
  }

  // Prefetch game-related data when viewing games
  async prefetchGameData() {
    const prefetchPromises = [
      // Prefetch current standings
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.standingsCurrent(),
        queryFn: () => standingsApi.getCurrent(),
        ...CACHE_CONFIG.DYNAMIC,
      }),

      // Prefetch live games
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.gamesLive(),
        queryFn: () => gamesApi.getLive(),
        ...CACHE_CONFIG.REALTIME,
      }),

      // Prefetch upcoming games
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.gamesUpcoming(),
        queryFn: () => gamesApi.getUpcoming(),
        ...CACHE_CONFIG.DYNAMIC,
      }),
    ];

    await Promise.allSettled(prefetchPromises);
  }

  // Prefetch championship data
  async prefetchChampionshipData(championshipSlug?: string) {
    const prefetchPromises = [
      // Prefetch champions list
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.champions(),
        queryFn: () => championsApi.getAll(),
        ...CACHE_CONFIG.STATIC,
      }),
    ];

    // If viewing specific championship, prefetch related data
    if (championshipSlug) {
      prefetchPromises.push(
        this.queryClient.prefetchQuery({
          queryKey: queryKeys.championshipChampions(championshipSlug),
          queryFn: () => championshipsApi.getChampions(championshipSlug),
          ...CACHE_CONFIG.STATIC,
        }),

        this.queryClient.prefetchQuery({
          queryKey: queryKeys.championshipStats(championshipSlug),
          queryFn: () => championshipsApi.getStats(championshipSlug),
          ...CACHE_CONFIG.STATIC,
        })
      );
    }

    await Promise.allSettled(prefetchPromises);
  }

  // Invalidate team-related cache when team data changes
  invalidateTeamCache(teamSlug: string) {
    const patterns = invalidationPatterns.team(teamSlug);
    patterns.forEach(pattern => {
      this.queryClient.invalidateQueries({ queryKey: pattern });
    });
  }

  // Invalidate game-related cache when game data changes
  invalidateGameCache(gameId: string) {
    const patterns = invalidationPatterns.game(gameId);
    patterns.forEach(pattern => {
      this.queryClient.invalidateQueries({ queryKey: pattern });
    });
  }

  // Invalidate player-related cache when player data changes
  invalidatePlayerCache(playerSlug: string) {
    const patterns = invalidationPatterns.player(playerSlug);
    patterns.forEach(pattern => {
      this.queryClient.invalidateQueries({ queryKey: pattern });
    });
  }

  // Invalidate standings cache
  invalidateStandingsCache() {
    const patterns = invalidationPatterns.standings();
    patterns.forEach(pattern => {
      this.queryClient.invalidateQueries({ queryKey: pattern });
    });
  }

  // Clear all cache
  clearAllCache() {
    this.queryClient.clear();
  }

  // Remove old cache entries
  async garbageCollect() {
    await this.queryClient.resumePausedMutations();
    this.queryClient.removeQueries({
      predicate: (query) => {
        const now = Date.now();
        const lastUpdated = query.state.dataUpdatedAt;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        return now - lastUpdated > maxAge;
      }
    });
  }

  // Warm up cache with essential data
  async warmupCache() {
    const warmupPromises = [
      // Essential static data
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.sports(),
        queryFn: async () => {
          // This would be implemented in sportsApi
          const response = await fetch('/api/core/sports/');
          return response.json();
        },
        ...CACHE_CONFIG.STATIC,
      }),

      // Current season standings
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.standingsCurrent(),
        queryFn: () => standingsApi.getCurrent(),
        ...CACHE_CONFIG.DYNAMIC,
      }),

      // Today's games
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.gamesToday(),
        queryFn: () => gamesApi.getToday(),
        ...CACHE_CONFIG.REALTIME,
      }),

      // Active teams
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.teams(),
        queryFn: () => teamsApi.getAll(),
        ...CACHE_CONFIG.SEMI_STATIC,
      }),
    ];

    await Promise.allSettled(warmupPromises);
  }

  // Background refresh for real-time data
  setupBackgroundRefresh() {
    // Refresh live games every 30 seconds
    setInterval(() => {
      this.queryClient.invalidateQueries({
        queryKey: queryKeys.gamesLive(),
        refetchType: 'active'
      });
    }, 30 * 1000);

    // Refresh today's games every 2 minutes
    setInterval(() => {
      this.queryClient.invalidateQueries({
        queryKey: queryKeys.gamesToday(),
        refetchType: 'active'
      });
    }, 2 * 60 * 1000);

    // Refresh current standings every 5 minutes
    setInterval(() => {
      this.queryClient.invalidateQueries({
        queryKey: queryKeys.standingsCurrent(),
        refetchType: 'active'
      });
    }, 5 * 60 * 1000);
  }

  // Get cache statistics
  getCacheStats() {
    const queryCache = this.queryClient.getQueryCache();
    const mutationCache = this.queryClient.getMutationCache();

    return {
      totalQueries: queryCache.getAll().length,
      totalMutations: mutationCache.getAll().length,
      cacheSize: this.estimateCacheSize(),
      hitRate: this.calculateHitRate(),
    };
  }

  private estimateCacheSize(): string {
    // Rough estimation of cache size
    const queries = this.queryClient.getQueryCache().getAll();
    const estimatedSize = queries.reduce((total, query) => {
      const dataStr = JSON.stringify(query.state.data);
      return total + (dataStr ? dataStr.length : 0);
    }, 0);

    // Convert to readable format
    if (estimatedSize < 1024) return `${estimatedSize} B`;
    if (estimatedSize < 1024 * 1024) return `${(estimatedSize / 1024).toFixed(1)} KB`;
    return `${(estimatedSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  private calculateHitRate(): number {
    // Calculate cache hit rate based on query states
    const queries = this.queryClient.getQueryCache().getAll();
    if (queries.length === 0) return 0;

    const hits = queries.filter(query =>
      query.state.status === 'success' &&
      query.state.dataUpdatedAt > 0
    ).length;

    return Math.round((hits / queries.length) * 100);
  }

  // Set cache preferences based on user connection
  optimizeForConnection(connectionType: 'slow' | 'fast' | 'offline') {
    const queryCache = this.queryClient.getQueryCache();

    switch (connectionType) {
      case 'slow':
        // Increase cache times, reduce refetch frequency
        // Note: Direct modification of query options is not supported in React Query v4
        // Instead, we should manage this through the QueryClient configuration
        break;

      case 'fast':
        // Default aggressive caching
        break;

      case 'offline':
        // Use cached data indefinitely
        // Note: Direct modification of query options is not supported in React Query v4
        // Instead, we should manage this through the QueryClient configuration
        break;
    }
  }
}

// Singleton instance
let cacheManagerInstance: CacheManager | null = null;

export const getCacheManager = (queryClient: QueryClient): CacheManager => {
  if (!cacheManagerInstance) {
    cacheManagerInstance = new CacheManager(queryClient);
  }
  return cacheManagerInstance;
};